import { useState, useEffect, useMemo } from "react";
import { Helmet } from "react-helmet-async";

import Navbar from "./Navbar";
import Footer from "./Footer";
import ProductCardSlant from "./ProductCard";
import bgPattern from "../assets/prodbag.jpeg";
import defaultImage from "../assets/default.png";
import { products as staticProducts } from "../data/products";

// Import your product images
import encrePrimaire from "../assets/encrePrimaire.png";
import encrePantone from "../assets/pantone.png";
import encreUV from "../assets/encreUV.png";
import plaques from "../assets/plaquesMachine.png";
import dorure from "../assets/dorure.png";
import pelliculage from "../assets/pelliculage.png";
import fildelin from "../assets/fildelin.png";
import reliureSpirale from "../assets/reliureSpirale.png";
import blanchet from "../assets/blanchet.png";
import maculante from "../assets/antimaculante.jpg";
import fildefer from "../assets/fildefer.png";
import reglette from "../assets/reglette.png";
import etirable from "../assets/etirable.png";
import aiguille from "../assets/aiguille.png";
import wash from "../assets/wash60.png";
import mouillage from "../assets/mouillage.png";
import ipa from "../assets/ipa.png";
import vernis from "../assets/vernis.png";
import gum from "../assets/gum.png";
import plateCleaner from "../assets/plateCleaner.png";

const FONTS = {
  clashDisplay: { fontFamily: "'Clash Display', sans-serif" },
  manrope: { fontFamily: "'Manrope', sans-serif" }
};

// Map filenames to imports
const imageMap = {
  "encrePrimaire.png": encrePrimaire,
  "encrePantone.png": encrePantone,
  "encreUV.png": encreUV,
  "plaques.png": plaques,
  "dorure.png": dorure,
  "pelliculage.png": pelliculage,
  "fildelin.png": fildelin,
  "reliureSpirale.png": reliureSpirale,
  "blanchet.png": blanchet,
  "maculante.png": maculante,
  "fildefer.png": fildefer,
  "reglette.png": reglette,
  "etirable.png": etirable,
  "aiguille.png": aiguille,
  "wash.png": wash,
  "mouillage.png": mouillage,
  "ipa.png": ipa,
  "vernis.png": vernis,
  "gum.png": gum,
  "plateCleaner.png": plateCleaner,
};

const CONFIG = {
  siteUrl: (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITE_URL) || 
           process.env.REACT_APP_SITE_URL || 
           "https://graphicup.tn",
};

// Skeleton Loader Component
const ProductSkeleton = () => (
  <div className="flex flex-col items-center relative animate-pulse">
    <div className="w-full h-64 bg-gray-200 rounded-[20px] mb-4"></div>
    <div className="w-3/4 h-6 bg-gray-200 rounded mb-2"></div>
    <div className="w-1/2 h-4 bg-gray-200 rounded"></div>
  </div>
);

// Back to Top Button
const BackToTop = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const toggleVisibility = () => {
      if (window.pageYOffset > 300) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  if (!isVisible) return null;

  return (
    <button
      onClick={scrollToTop}
      className="fixed bottom-8 right-8 z-50 p-4 bg-[#158DC1] text-white rounded-full shadow-2xl hover:scale-110 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-[#158DC1] focus:ring-opacity-50"
      aria-label="Retour en haut"
    >
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
      </svg>
    </button>
  );
};

export default function ProductsPage() {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Group products by category
  const groupedProducts = useMemo(() => {
    const cats = [...new Set(staticProducts.map((p) => p.category))];
    return cats.map((cat) => ({
      category: cat,
      items: staticProducts.filter((p) => p.category === cat),
    }));
  }, []);



  return (
    <div className="w-full bg-black relative overflow-x-hidden">
      <Helmet>
        <html lang="fr" />
        <title>Nos Produits – Graphic Up | Impression Offset Professionnelle</title>
        <meta name="description" content="Découvrez notre gamme complète de produits pour l'impression offset: encres premium, plaques, additifs chimiques, consommables et fournitures de finition. Qualité professionnelle garantie." />
        <link rel="canonical" href={`${CONFIG.siteUrl}/produits`} />
        <meta property="og:title" content="Nos Produits – Graphic Up" />
        <meta property="og:description" content="Encres, plaques, additifs chimiques, consommables et fournitures de finition pour l'impression offset" />
        <meta property="og:url" content={`${CONFIG.siteUrl}/produits`} />
        <meta property="og:type" content="website" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      {/* Skip to main content */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-white p-2 z-50 rounded-lg font-bold"
      >
        Aller au contenu principal
      </a>

      <Navbar />

      {/* Background Pattern */}
      <div className="absolute inset-0 z-0">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(to bottom, black 0%, transparent 10%, transparent 50%, black 80%), url(${bgPattern})`,
            backgroundRepeat: "no-repeat",
            backgroundSize: "120%",
            backgroundPosition: "top center",
          }}
        />
      </div>

      {/* Overlay */}
      <div
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: `
            linear-gradient(
              to bottom,
              rgba(0,0,0,0.5) 0%,
              rgba(0,0,0,0.6) 20%,
              rgba(0,0,0,0.7) 50%,
              rgba(0,0,0,0.6) 75%,
              rgba(0,0,0,0.5) 90%
            )
          `,
        }}
      />

      {/* Main Content */}
      <main id="main-content" className="relative z-10 py-12 flex flex-col gap-32" aria-label="Liste des produits">
        <div className={`relative z-10 mt-10 md:mt-24 max-w-8xl px-4 py-24 text-center mx-auto transition-all duration-1000 ${
          mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}>
          <p className="text-xl sm:text-2xl md:text-h3 font-bold text-white mb-2" style={FONTS.clashDisplay}>
            Nos Produits
          </p>

          <h1 className="text-3xl sm:text-4xl md:text-4xl lg:text-h1 font-bold text-white m-4 leading-tight" style={{...FONTS.clashDisplay, textShadow: '0 2px 20px rgba(0,0,0,0.5)'}}>
            Découvrez notre gamme complète
          </h1>

          <p className="text-base sm:text-lg md:text-xl lg:text-2xl italic text-white max-w-3xl mx-auto leading-relaxed" style={{...FONTS.manrope, textShadow: '0 2px 15px rgba(0,0,0,0.5)'}}>
            Qualité, fiabilité et innovation pour tous vos besoins en impression, finitions et entretien de presses.
          </p>
        </div>

        {groupedProducts.length === 0 ? (
          <div className="text-center text-white text-xl py-20">
            <div className="text-6xl mb-4">📦</div>
            <p className="text-2xl font-bold mb-2" style={FONTS.clashDisplay}>
              Aucun produit disponible
            </p>
            <p style={FONTS.manrope}>
              Nos produits arrivent bientôt
            </p>
          </div>
        ) : (
          groupedProducts.map((group, idx) => (
            <section 
              key={group.category} 
              className="w-full flex justify-center px-6"
              style={{
                animation: `fadeIn 0.8s ease-out ${idx * 0.15}s both`
              }}
            >
              <div className="bg-white rounded-[60px] shadow-2xl max-w-8xl w-full px-6 lg:px-16 py-12 hover:shadow-3xl transition-shadow duration-500">
                <h2 className="text-2xl sm:text-3xl text-left md:text-4xl font-bold mb-12 text-black" style={FONTS.clashDisplay}>
                  {group.category}
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12">
                  {group.items.map((product) => (
                    <div
                      key={product.id}
                      className="flex flex-col items-center relative"
                      style={{
                        animation: 'fadeInUp 0.6s ease-out both',
                        animationDelay: `${Math.random() * 0.3}s`
                      }}
                    >
                      <ProductCardSlant
                        id={product.id}
                        {...product}
                        image={imageMap[product.image] || defaultImage}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </section>
          ))
        )}
      </main>

      <BackToTop />
      <Footer />

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        html {
          scroll-behavior: smooth;
        }

        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
          
          html {
            scroll-behavior: auto;
          }
        }
      `}</style>
    </div>
  );
}